import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowRight, Clock, Coins, MapPin, ChevronLeft, Users, ShoppingBag, Check, Train, ExternalLink } from "lucide-react";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import Footer from "../components/Footer";
import SafeImage from "../components/SafeImage";
import { trainlineSearchUrl, bookingCityUrl, interrailPassUrl, trackAffiliateClick } from "../lib/affiliates";
import BookmarkHeart from "../components/BookmarkHeart";

export default function FeaturedRouteDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [route, setRoute] = useState(null);
  const { items, addItem } = useCart();
  const inCart = route ? items.some((x) => x.kind === "tour" && x.id === route.slug) : false;

  useEffect(() => {
    api.get(`/featured-routes/${slug}`).then((r) => setRoute(r.data)).catch(() => navigate("/routes"));
  }, [slug, navigate]);

  if (!route) {
    return <div className="pt-32 text-center text-cream-dim font-display tracking-widest">Loading route…</div>;
  }

  return (
    <div data-testid="featured-route-detail" className="pt-safe-navbar">
      <section className="relative min-h-[55vh] flex items-end overflow-hidden">
        <SafeImage src={route.hero} alt={route.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/80 to-ink/30" />
        <div className="relative max-w-[1400px] mx-auto px-6 py-12 w-full">
          <Link to="/routes" className="inline-flex items-center gap-1 text-cream-dim hover:text-gold text-sm mb-4">
            <ChevronLeft className="h-4 w-4" /> All routes
          </Link>
          <div className="iq-overline mb-2">{route.vibe} · {route.duration_days} days</div>
          <h1 className="font-display text-cream text-5xl md:text-7xl tracking-tight leading-none">{route.name}</h1>
          <p className="font-serif italic text-cream/90 text-xl mt-3 max-w-2xl">{route.tagline}</p>
          <div className="flex flex-wrap items-center gap-3 mt-6">
            <button
              data-testid="route-add-to-cart"
              onClick={() => {
                if (inCart) {
                  navigate("/cart");
                } else {
                  addItem({
                    id: route.slug,
                    kind: "tour",
                    name: route.name,
                    sub: `${route.duration_days} days · ${route.cities.length} cities`,
                    price_eur: route.est_price_eur,
                    qty: 1,
                    image: route.hero,
                    meta: { cities: route.cities, vibe: route.vibe },
                  });
                  toast.success(`Added "${route.name}" to cart`);
                }
              }}
              className="iq-btn-primary text-base"
            >
              {inCart ? <><Check className="h-4 w-4" /> In cart · View cart</> : <><ShoppingBag className="h-4 w-4" /> Add to cart · €{route.est_price_eur}</>}
            </button>
            <Link to={`/groups?route=${route.slug}`} className="iq-btn-secondary">
              <Users className="h-4 w-4" /> Group inquiry
            </Link>
            <BookmarkHeart kind="tour" slug={route.slug} label={route.name} size="lg" />
          </div>
        </div>
      </section>

      <div className="max-w-[1100px] mx-auto px-6 py-12 space-y-10">
        <p className="font-serif text-cream text-xl leading-relaxed">{route.description}</p>

        <div className="grid sm:grid-cols-3 gap-4">
          <div className="iq-card p-5 text-center">
            <Clock className="h-5 w-5 text-gold mx-auto mb-2" />
            <div className="font-display text-2xl text-cream">{route.duration_days}</div>
            <div className="text-[10px] text-cream-dim uppercase tracking-widest">Days</div>
          </div>
          <div className="iq-card p-5 text-center">
            <MapPin className="h-5 w-5 text-gold mx-auto mb-2" />
            <div className="font-display text-2xl text-cream">{route.cities.length}</div>
            <div className="text-[10px] text-cream-dim uppercase tracking-widest">Cities</div>
          </div>
          <div className="iq-card p-5 text-center">
            <Coins className="h-5 w-5 text-gold mx-auto mb-2" />
            <div className="font-display text-2xl text-cream">€{route.est_price_eur}</div>
            <div className="text-[10px] text-cream-dim uppercase tracking-widest">From, per person</div>
          </div>
        </div>

        <div>
          <div className="iq-overline mb-4">Itinerary</div>
          <div className="space-y-3">
            {(route.cities_data || []).map((c, i) => {
              const nextCity = (route.cities_data || [])[i + 1];
              const railUrl = nextCity ? trainlineSearchUrl(c.name, nextCity.name, "") : null;
              return (
                <div key={c.slug} className="iq-card overflow-hidden hover:border-gold/40 transition-colors" data-testid={`route-city-${c.slug}`}>
                  <Link to={`/city/${c.slug}`} className="flex items-stretch">
                    <div className="w-24 sm:w-40 shrink-0 relative">
                      <SafeImage src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent to-panel/70" />
                    </div>
                    <div className="flex-1 p-5 flex items-center justify-between gap-4">
                      <div>
                        <div className="iq-overline text-xs">Stop {i + 1} · {c.country}</div>
                        <h3 className="font-display text-cream text-2xl md:text-3xl tracking-wider">{c.name}</h3>
                        <p className="text-cream-dim text-sm font-serif italic mt-1 line-clamp-1">{c.tagline}</p>
                      </div>
                      <ArrowRight className="h-5 w-5 text-gold shrink-0" />
                    </div>
                  </Link>
                  {/* Affiliate row */}
                  <div className="border-t border-panel-border bg-ink/60 px-5 py-3 flex items-center gap-2 flex-wrap text-xs">
                    <span className="text-cream-dim/70 mr-1">Book live:</span>
                    <a
                      href={bookingCityUrl(c.name)}
                      target="_blank"
                      rel="sponsored noopener noreferrer"
                      onClick={() => trackAffiliateClick("booking", { city: c.name, source: "featured_route" })}
                      data-testid={`aff-stay-${c.slug}`}
                      className="text-gold hover:text-cream inline-flex items-center gap-1"
                    >
                      Stays in {c.name} <ExternalLink className="h-3 w-3" />
                    </a>
                    {railUrl && (
                      <>
                        <span className="text-cream-dim/30">·</span>
                        <a
                          href={railUrl}
                          target="_blank"
                          rel="sponsored noopener noreferrer"
                          onClick={() => trackAffiliateClick("trainline", { from: c.name, to: nextCity.name, source: "featured_route" })}
                          data-testid={`aff-rail-${c.slug}`}
                          className="text-gold hover:text-cream inline-flex items-center gap-1"
                        >
                          <Train className="h-3 w-3" /> {c.name} → {nextCity.name} on Trainline <ExternalLink className="h-3 w-3" />
                        </a>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <a
          href={interrailPassUrl("global")}
          target="_blank"
          rel="sponsored noopener noreferrer"
          onClick={() => trackAffiliateClick("interrail", { route: route.slug, source: "featured_route_pass_upsell" })}
          data-testid="route-pass-cta"
          className="block iq-card p-5 md:p-6 bg-gradient-to-r from-gold/15 via-gold/5 to-transparent border-gold/40 hover:border-gold/70 transition-colors group"
        >
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="iq-overline text-gold">Consider an Interrail Pass</div>
              <p className="text-cream text-sm mt-1">
                {route.cities?.length || 0} cities · roughly the sweet spot where a Global Pass beats single tickets and gives you the freedom to detour.
              </p>
            </div>
            <ArrowRight className="h-5 w-5 text-gold opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all shrink-0" />
          </div>
        </a>

        <div className="iq-card p-6 md:p-8 grid md:grid-cols-[1fr_auto] gap-4 items-center">
          <div>
            <div className="iq-overline mb-2"><Users className="inline h-3 w-3 mr-1" /> Travelling as a group?</div>
            <h3 className="font-display text-cream text-2xl tracking-wider">Book this route for your school or team</h3>
            <p className="text-cream-dim text-sm max-w-2xl mt-1">Bulk-rate accommodation, supervised minors, and a single invoice for groups of 10–500.</p>
          </div>
          <Link to={`/groups?route=${route.slug}`} className="iq-btn-primary" data-testid="route-group-cta">
            Request group quote <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
